import type { Dict } from '../../i18n';

const dict: Dict = {
  en: {
    // ── Page heading / topbar ──────────────────────────────────────────
    heading: 'Agents',
    /** "12 total · 10 active · 1 blocked · 1 invite pending" */
    counts_summary: '{{total}} total · {{active}} active · {{blocked}} blocked · {{invited}} invite pending',
    search_placeholder: 'Search…',
    export_report: 'Export report',
    export_preparing: 'Preparing…',
    invite_agent: '+ Invite agent',

    // ── Table column headers ───────────────────────────────────────────
    col_agent: 'Agent',
    col_office: 'Office',
    col_status: 'Status',
    col_orders_30d: 'Orders 30d',
    col_mtd_volume: 'MTD volume',
    col_clients: 'Clients',
    col_activity: 'Activity',
    col_conv: 'Conv',
    col_last_login: 'Last login',
    /** Row action that opens the agent profile/edit drawer. */
    btn_edit: 'Edit',

    // ── Loading / empty / error ────────────────────────────────────────
    loading_agents: 'Loading agents…',
    empty_title: 'No agents yet',
    empty_hint: 'Invite an agent to start placing orders.',

    // ── "never" last-login label ───────────────────────────────────────
    never: 'never',

    // ── Agent drawer — header / profile section ────────────────────────
    drawer_close: 'Close',
    /** "Office · Role · since Mon YYYY" */
    since: 'since',

    // ── Contact fields (shared by Profile + Invite) ────────────────────
    field_first_name: 'First name',
    field_last_name: 'Last name',
    field_phone: 'Phone number',
    field_office: 'Office',
    field_commission: 'Commission rate (%)',
    commission_hint: '0–50 %, two decimals. Visible to the Super Admin only; changes are logged and apply to new orders.',
    office_placeholder: 'Select office',

    // ── Agent drawer — Profile section ─────────────────────────────────
    section_profile: 'Profile',
    btn_save_profile: 'Save profile',
    btn_save_profile_loading: 'Saving…',
    /** Inline hint shown when office or phone are still empty (both required for agents). */
    required_office_phone_hint: 'Office and phone are required for agents.',

    // ── Agent drawer — Role section ────────────────────────────────────
    section_role: 'Role',
    role_agent_locked: "An agent's role cannot be changed.",

    // ── Agent drawer — Account section ────────────────────────────────
    section_account: 'Account',
    btn_send_reset_email: 'Reset password (send email)',
    btn_send_reset_email_loading: 'Sending reset email…',
    btn_set_temp_password: 'Set temporary password',
    btn_set_temp_password_loading: 'Resetting password…',
    btn_reset_2fa: 'Reset 2FA',
    btn_reset_2fa_loading: 'Resetting 2FA…',
    btn_export_orders_csv: 'Export their orders CSV',
    btn_export_orders_loading: 'Preparing…',

    // ── Agent drawer — Block / Unblock / Delete ────────────────────────
    btn_block: 'Block agent',
    btn_block_loading: 'Blocking…',
    btn_unblock: 'Unblock agent',
    btn_unblock_loading: 'Unblocking…',
    btn_delete: 'Delete',
    btn_delete_loading: 'Deleting…',
    blocking_note: 'Blocking preserves history. Deleting reassigns orders to "former agents".',

    // ── Agent drawer — 2FA tooltip ─────────────────────────────────────
    tooltip_2fa_not_enrolled: 'This agent has not enrolled 2FA yet',

    // ── Agent drawer — window.prompt / window.confirm strings ─────────
    prompt_set_password: 'Set a temporary password for this agent (≥12 chars, with an upper- and lower-case letter and a digit — they must change it):',
    confirm_reset_2fa: 'Reset 2FA? The agent must re-enrol an authenticator on next login.',
    confirm_delete: 'Delete {{name}}? Their orders are reassigned to "former agents". This cannot be undone.',

    // ── Agent drawer — notice / success messages ───────────────────────
    notice_profile_saved_ok: 'Profile saved.',
    notice_reset_email_ok: 'Password-reset email requested for {{email}}.',
    notice_password_reset_ok: 'Password reset — the agent’s sessions were revoked.',
    notice_2fa_reset_ok: '2FA reset — the agent will re-enrol on next login.',
    notice_export_failed: 'Export failed.',
    notice_no_2fa: 'This agent has not enrolled 2FA yet — there is nothing to reset.',

    // ── Password-policy error keys ─────────────────────────────────────
    pw_too_short: 'Password must be at least 12 characters.',
    pw_no_lowercase: 'Password must contain a lowercase letter.',
    pw_no_uppercase: 'Password must contain an uppercase letter.',
    pw_no_digit: 'Password must contain a digit.',

    // ── Invite drawer ──────────────────────────────────────────────────
    invite_drawer_heading: 'Add agent',
    invite_label_full_name: 'Full name',
    invite_label_email: 'Email',
    invite_label_office: 'Office',
    invite_label_role: 'Role',
    invite_hint:
      'A temporary password is generated and shown once. The agent sets their own password on first login; 2FA is optional (they can enable it later in Settings).',
    invite_cancel: 'Cancel',
    invite_submit: 'Create agent',
    invite_submit_loading: 'Creating…',
    invite_created_heading: 'Agent created',
    invite_created_hint: 'Share this temporary password with {{email}} — it is shown only once.',
    invite_temp_password: 'Temporary password',
    invite_temp_password_note:
      'The agent must change it on first login. 2FA is optional — they can enable it in Settings.',
    invite_done: 'Done',
  },

  es: {
    // ── Page heading / topbar ──────────────────────────────────────────
    heading: 'Agentes',
    counts_summary: '{{total}} total · {{active}} activos · {{blocked}} bloqueados · {{invited}} invitación pendiente',
    search_placeholder: 'Buscar…',
    export_report: 'Exportar informe',
    export_preparing: 'Preparando…',
    invite_agent: '+ Invitar agente',

    // ── Table column headers ───────────────────────────────────────────
    col_agent: 'Agente',
    col_office: 'Oficina',
    col_status: 'Estado',
    col_orders_30d: 'Pedidos 30d',
    col_mtd_volume: 'Volumen del mes',
    col_clients: 'Clientes',
    col_activity: 'Actividad',
    col_conv: 'Conv.',
    col_last_login: 'Último acceso',
    /** Row action that opens the agent profile/edit drawer. */
    btn_edit: 'Editar',

    // ── Loading / empty / error ────────────────────────────────────────
    loading_agents: 'Cargando agentes…',
    empty_title: 'Aún no hay agentes',
    empty_hint: 'Invita a un agente para empezar a crear pedidos.',

    // ── "never" last-login label ───────────────────────────────────────
    never: 'nunca',

    // ── Agent drawer — header / profile section ────────────────────────
    drawer_close: 'Cerrar',
    since: 'desde',

    // ── Contact fields (shared by Profile + Invite) ────────────────────
    field_first_name: 'Nombre',
    field_last_name: 'Apellido',
    field_phone: 'Número de teléfono',
    field_office: 'Oficina',
    field_commission: 'Comisión (%)',
    commission_hint: '0–50 %, dos decimales. Solo la ve el Super Admin; los cambios quedan registrados y aplican a pedidos nuevos.',
    office_placeholder: 'Selecciona la oficina',

    // ── Agent drawer — Profile section ─────────────────────────────────
    section_profile: 'Perfil',
    btn_save_profile: 'Guardar perfil',
    btn_save_profile_loading: 'Guardando…',
    /** Inline hint shown when office or phone are still empty (both required for agents). */
    required_office_phone_hint: 'La oficina y el teléfono son obligatorios para los agentes.',

    // ── Agent drawer — Role section ────────────────────────────────────
    section_role: 'Rol',
    role_agent_locked: 'El rol de agente no se puede cambiar.',

    // ── Agent drawer — Account section ────────────────────────────────
    section_account: 'Cuenta',
    btn_send_reset_email: 'Restablecer contraseña (enviar correo)',
    btn_send_reset_email_loading: 'Enviando correo de restablecimiento…',
    btn_set_temp_password: 'Establecer contraseña temporal',
    btn_set_temp_password_loading: 'Restableciendo contraseña…',
    btn_reset_2fa: 'Restablecer 2FA',
    btn_reset_2fa_loading: 'Restableciendo 2FA…',
    btn_export_orders_csv: 'Exportar sus pedidos CSV',
    btn_export_orders_loading: 'Preparando…',

    // ── Agent drawer — Block / Unblock / Delete ────────────────────────
    btn_block: 'Bloquear agente',
    btn_block_loading: 'Bloqueando…',
    btn_unblock: 'Desbloquear agente',
    btn_unblock_loading: 'Desbloqueando…',
    btn_delete: 'Eliminar',
    btn_delete_loading: 'Eliminando…',
    blocking_note: 'Bloquear preserva el historial. Eliminar reasigna los pedidos a "antiguos agentes".',

    // ── Agent drawer — 2FA tooltip ─────────────────────────────────────
    tooltip_2fa_not_enrolled: 'Este agente aún no ha activado 2FA',

    // ── Agent drawer — window.prompt / window.confirm strings ─────────
    prompt_set_password: 'Establece una contraseña temporal para este agente (≥12 caracteres, con mayúscula, minúscula y dígito — debe cambiarla):',
    confirm_reset_2fa: '¿Restablecer 2FA? El agente deberá volver a registrar un autenticador en el próximo inicio de sesión.',
    confirm_delete: '¿Eliminar {{name}}? Sus pedidos serán reasignados a "antiguos agentes". Esta acción no se puede deshacer.',

    // ── Agent drawer — notice / success messages ───────────────────────
    notice_profile_saved_ok: 'Perfil guardado.',
    notice_reset_email_ok: 'Correo de restablecimiento solicitado para {{email}}.',
    notice_password_reset_ok: 'Contraseña restablecida — las sesiones del agente han sido revocadas.',
    notice_2fa_reset_ok: '2FA restablecido — el agente deberá volver a registrarse en el próximo inicio de sesión.',
    notice_export_failed: 'Error al exportar.',
    notice_no_2fa: 'Este agente aún no ha activado 2FA — nada que restablecer.',

    // ── Password-policy error keys ─────────────────────────────────────
    pw_too_short: 'La contraseña debe tener al menos 12 caracteres.',
    pw_no_lowercase: 'La contraseña debe contener una letra minúscula.',
    pw_no_uppercase: 'La contraseña debe contener una letra mayúscula.',
    pw_no_digit: 'La contraseña debe contener un dígito.',

    // ── Invite drawer ──────────────────────────────────────────────────
    invite_drawer_heading: 'Añadir agente',
    invite_label_full_name: 'Nombre completo',
    invite_label_email: 'Correo electrónico',
    invite_label_office: 'Oficina',
    invite_label_role: 'Rol',
    invite_hint:
      'Se genera una contraseña temporal que se muestra una sola vez. El agente establece su propia contraseña en el primer inicio de sesión; el 2FA es opcional (puede activarlo luego en Ajustes).',
    invite_cancel: 'Cancelar',
    invite_submit: 'Crear agente',
    invite_submit_loading: 'Creando…',
    invite_created_heading: 'Agente creado',
    invite_created_hint:
      'Comparte esta contraseña temporal con {{email}} — se muestra solo una vez.',
    invite_temp_password: 'Contraseña temporal',
    invite_temp_password_note:
      'El agente debe cambiarla en el primer inicio de sesión. El 2FA es opcional — puede activarlo en Ajustes.',
    invite_done: 'Hecho',
  },

  de: {
    // ── Page heading / topbar ──────────────────────────────────────────
    heading: 'Agenten',
    counts_summary: '{{total}} gesamt · {{active}} aktiv · {{blocked}} gesperrt · {{invited}} Einladung ausstehend',
    search_placeholder: 'Suchen…',
    export_report: 'Bericht exportieren',
    export_preparing: 'Wird vorbereitet…',
    invite_agent: '+ Agent einladen',

    // ── Table column headers ───────────────────────────────────────────
    col_agent: 'Agent',
    col_office: 'Niederlassung',
    col_status: 'Status',
    col_orders_30d: 'Bestellungen 30T',
    col_mtd_volume: 'Volumen lfd. Monat',
    col_clients: 'Kunden',
    col_activity: 'Aktivität',
    col_conv: 'Konv.',
    col_last_login: 'Letzte Anmeldung',
    /** Row action that opens the agent profile/edit drawer. */
    btn_edit: 'Bearbeiten',

    // ── Loading / empty / error ────────────────────────────────────────
    loading_agents: 'Agenten werden geladen…',
    empty_title: 'Noch keine Agenten',
    empty_hint: 'Laden Sie einen Agenten ein, um mit der Bestellung zu beginnen.',

    // ── "never" last-login label ───────────────────────────────────────
    never: 'nie',

    // ── Agent drawer — header / profile section ────────────────────────
    drawer_close: 'Schließen',
    since: 'seit',

    // ── Contact fields (shared by Profile + Invite) ────────────────────
    field_first_name: 'Vorname',
    field_last_name: 'Nachname',
    field_phone: 'Telefonnummer',
    field_office: 'Niederlassung',
    field_commission: 'Provisionssatz (%)',
    commission_hint: '0–50 %, zwei Dezimalstellen. Nur für den Super Admin sichtbar; Änderungen werden protokolliert und gelten für neue Aufträge.',
    office_placeholder: 'Niederlassung auswählen',

    // ── Agent drawer — Profile section ─────────────────────────────────
    section_profile: 'Profil',
    btn_save_profile: 'Profil speichern',
    btn_save_profile_loading: 'Wird gespeichert…',
    /** Inline hint shown when office or phone are still empty (both required for agents). */
    required_office_phone_hint: 'Niederlassung und Telefon sind für Agenten erforderlich.',

    // ── Agent drawer — Role section ────────────────────────────────────
    section_role: 'Rolle',
    role_agent_locked: 'Die Rolle eines Agenten kann nicht geändert werden.',

    // ── Agent drawer — Account section ────────────────────────────────
    section_account: 'Konto',
    btn_send_reset_email: 'Passwort zurücksetzen (E-Mail senden)',
    btn_send_reset_email_loading: 'Zurücksetzungs-E-Mail wird gesendet…',
    btn_set_temp_password: 'Temporäres Passwort festlegen',
    btn_set_temp_password_loading: 'Passwort wird zurückgesetzt…',
    btn_reset_2fa: '2FA zurücksetzen',
    btn_reset_2fa_loading: '2FA wird zurückgesetzt…',
    btn_export_orders_csv: 'Bestellungen als CSV exportieren',
    btn_export_orders_loading: 'Wird vorbereitet…',

    // ── Agent drawer — Block / Unblock / Delete ────────────────────────
    btn_block: 'Agent sperren',
    btn_block_loading: 'Wird gesperrt…',
    btn_unblock: 'Agent entsperren',
    btn_unblock_loading: 'Wird entsperrt…',
    btn_delete: 'Löschen',
    btn_delete_loading: 'Wird gelöscht…',
    blocking_note: 'Das Sperren bewahrt den Verlauf. Beim Löschen werden Bestellungen „ehemaligen Agenten" zugewiesen.',

    // ── Agent drawer — 2FA tooltip ─────────────────────────────────────
    tooltip_2fa_not_enrolled: 'Dieser Agent hat 2FA noch nicht eingerichtet',

    // ── Agent drawer — window.prompt / window.confirm strings ─────────
    prompt_set_password: 'Legen Sie ein temporäres Passwort für diesen Agenten fest (≥12 Zeichen, mit einem Groß- und einem Kleinbuchstaben sowie einer Ziffer — er muss es ändern):',
    confirm_reset_2fa: '2FA zurücksetzen? Der Agent muss bei der nächsten Anmeldung einen Authenticator neu einrichten.',
    confirm_delete: '{{name}} löschen? Die Bestellungen werden „ehemaligen Agenten" zugewiesen. Dies kann nicht rückgängig gemacht werden.',

    // ── Agent drawer — notice / success messages ───────────────────────
    notice_profile_saved_ok: 'Profil gespeichert.',
    notice_reset_email_ok: 'Passwort-Zurücksetzungs-E-Mail für {{email}} angefordert.',
    notice_password_reset_ok: 'Passwort zurückgesetzt — die Sitzungen des Agenten wurden widerrufen.',
    notice_2fa_reset_ok: '2FA zurückgesetzt — der Agent richtet es bei der nächsten Anmeldung neu ein.',
    notice_export_failed: 'Export fehlgeschlagen.',
    notice_no_2fa: 'Dieser Agent hat 2FA noch nicht eingerichtet — es gibt nichts zurückzusetzen.',

    // ── Password-policy error keys ─────────────────────────────────────
    pw_too_short: 'Das Passwort muss mindestens 12 Zeichen lang sein.',
    pw_no_lowercase: 'Das Passwort muss einen Kleinbuchstaben enthalten.',
    pw_no_uppercase: 'Das Passwort muss einen Großbuchstaben enthalten.',
    pw_no_digit: 'Das Passwort muss eine Ziffer enthalten.',

    // ── Invite drawer ──────────────────────────────────────────────────
    invite_drawer_heading: 'Agent hinzufügen',
    invite_label_full_name: 'Vollständiger Name',
    invite_label_email: 'E-Mail',
    invite_label_office: 'Niederlassung',
    invite_label_role: 'Rolle',
    invite_hint:
      'Ein temporäres Passwort wird generiert und einmalig angezeigt. Der Agent legt bei der ersten Anmeldung sein eigenes Passwort fest; 2FA ist optional (er kann es später in den Einstellungen aktivieren).',
    invite_cancel: 'Abbrechen',
    invite_submit: 'Agent erstellen',
    invite_submit_loading: 'Wird erstellt…',
    invite_created_heading: 'Agent erstellt',
    invite_created_hint: 'Teilen Sie dieses temporäre Passwort mit {{email}} — es wird nur einmal angezeigt.',
    invite_temp_password: 'Temporäres Passwort',
    invite_temp_password_note:
      'Der Agent muss es bei der ersten Anmeldung ändern. 2FA ist optional — er kann es in den Einstellungen aktivieren.',
    invite_done: 'Fertig',
  },
};

export default dict;
